import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowDown,
  Check,
  Download,
  Eye,
  File as FileIcon,
  Loader2,
  Mic,
  MessageSquare,
  Paperclip,
  Pause,
  Pencil,
  Pin,
  Play,
  Plus,
  Reply,
  Send,
  Smile,
  Square,
  Trash2,
  Users as UsersIcon,
  X,
} from "lucide-react";
import { api, apiError, downloadFile } from "@/lib/api";
import { emitChange, MESSAGES_CHANGED, onChange } from "@/lib/events";
import { playSelectedSound } from "@/lib/sound";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { useConfirm } from "@/components/Confirm";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { useDelayedLoading } from "@/lib/useDelayedLoading";
import Layout from "@/components/Layout";
import NewConversationDialog from "@/components/NewConversationDialog";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// The real-time path is push-driven (see App.js's NotificationBridge, which
// re-fires MESSAGES_CHANGED the instant a push notification arrives in this
// tab, refreshing both the sidebar and whichever thread is open below) —
// this interval is only the fallback for anyone without notifications
// turned on, so 3s is plenty snappy without hammering the server.
const POLL_MS = 3000;
const MAX_FILES = 10;
const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "😮", "👀"];

// A curated set grouped like a normal chat app's picker (Smileys, Gestures,
// …) rather than one giant scroll — small enough to ship inline with no new
// dependency, wide enough to cover what people actually reach for.
const EMOJI_GROUPS = [
  {
    label: "Smileys",
    icon: "😀",
    emojis: [
      "😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃", "😉", "😊",
      "😇", "🥰", "😍", "🤩", "😘", "😗", "😚", "😙", "😋", "😛", "😜", "🤪",
      "😝", "🤑", "🤗", "🤭", "🤫", "🤔", "🤐", "🤨", "😐", "😑", "😶", "😏",
      "😒", "🙄", "😬", "🤥", "😌", "😔", "😪", "🤤", "😴", "😷", "🤒", "🤕",
      "🤢", "🤮", "🥵", "🥶", "😵", "🤯", "🥳", "😎", "🤓", "🧐", "😢", "😭",
      "😡", "🤬", "😱", "🥺",
    ],
  },
  {
    label: "Gestures",
    icon: "👍",
    emojis: [
      "👍", "👎", "👌", "🤞", "✌️", "🤟", "🤘", "👏", "🙌", "👐", "🤲", "🙏",
      "✋", "🖐️", "👋", "🤙", "💪", "🫶", "👊", "✊", "🤝", "☝️", "👆", "👇",
      "👈", "👉", "🖖",
    ],
  },
  {
    label: "Hearts",
    icon: "❤️",
    emojis: [
      "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❣️", "💕",
      "💞", "💓", "💗", "💖", "💘", "💝", "💟", "😻",
    ],
  },
  {
    label: "Animals",
    icon: "🐶",
    emojis: [
      "🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯", "🦁", "🐮",
      "🐷", "🐸", "🐵", "🐔", "🐧", "🐦", "🦆", "🦅", "🦉", "🐴", "🦄", "🐝",
      "🐛", "🦋", "🐌", "🐢", "🐍", "🐙", "🐠", "🐳", "🐬",
    ],
  },
  {
    label: "Food",
    icon: "🍕",
    emojis: [
      "🍎", "🍌", "🍇", "🍓", "🍒", "🍑", "🍍", "🥝", "🍅", "🥑", "🍕", "🍔",
      "🍟", "🌭", "🍿", "🧀", "🥐", "🍞", "🥞", "🧇", "🍳", "🥗", "🍜", "🍣",
      "🍩", "🍪", "🎂", "🍰", "🍫", "🍬", "🍭", "☕", "🍵", "🥤", "🍺", "🍷",
    ],
  },
  {
    label: "Activities",
    icon: "⚽",
    emojis: [
      "⚽", "🏀", "🏈", "⚾", "🎾", "🏐", "🏉", "🎱", "🏓", "🏸", "🥊", "🎯",
      "🎮", "🎲", "🧩", "🎸", "🎤", "🎧", "🎨", "🎬", "🏆", "🥇", "🎉", "🎊",
      "🎁", "🔥", "✨", "⭐", "💯", "✅",
    ],
  },
];

function EmojiPicker({ onPick }) {
  const [group, setGroup] = useState(0);
  return (
    <div className="w-[276px]">
      <div className="flex gap-0.5 border-b border-line px-1 pb-1.5 mb-1.5">
        {EMOJI_GROUPS.map((g, i) => (
          <button
            key={g.label}
            type="button"
            onClick={() => setGroup(i)}
            title={g.label}
            className={cn(
              "flex-1 rounded-lg py-1 text-[15px] transition-colors",
              i === group ? "bg-brand-light" : "hover:bg-brand-light/50"
            )}
          >
            {g.icon}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5 max-h-[216px] overflow-y-auto px-0.5 pb-1 thin-scroll">
        {EMOJI_GROUPS[group].emojis.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => onPick(e)}
            className="w-9 h-9 flex items-center justify-center text-[19px] rounded-lg hover:bg-brand-light/60 transition-colors"
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}

// A small built-in pack rather than a live GIF/sticker search — no external
// API key to manage, no third-party service that can go down, and nothing
// unmoderated can land in a client conversation. Files live in
// public/stickers/, colored from the app's own accent swatches so the pack
// reads as part of the brand rather than a bolted-on asset set.
const STICKERS = [
  { file: "on-it.png", label: "On it" },
  { file: "thank-you.png", label: "Thank you!" },
  { file: "lets-go.png", label: "Let's go" },
  { file: "nice-work.png", label: "Nice work!" },
  { file: "got-it.png", label: "Got it" },
  { file: "one-sec.png", label: "One sec…" },
  { file: "oops.png", label: "Oops!" },
  { file: "love-this.png", label: "Love this" },
  { file: "brb.png", label: "brb" },
  { file: "lol.png", label: "lol" },
  { file: "looking-good.png", label: "Looking good" },
  { file: "on-my-way.png", label: "On my way" },
];

function StickerPicker({ onPick, busy }) {
  return (
    <div className="w-[276px] grid grid-cols-3 gap-1.5 max-h-[260px] overflow-y-auto p-1 thin-scroll">
      {STICKERS.map((s) => (
        <button
          key={s.file}
          type="button"
          disabled={busy}
          onClick={() => onPick(s)}
          title={s.label}
          className="rounded-xl overflow-hidden hover:scale-105 transition-transform disabled:opacity-50 disabled:pointer-events-none"
        >
          <img src={`/stickers/${s.file}`} alt={s.label} className="w-full h-auto" draggable={false} />
        </button>
      ))}
    </div>
  );
}

function when(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  // The date has to come from the same local clock as the time — formatDate
  // reads only the UTC date part, so evening messages showed tomorrow's date.
  const date = d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return today ? time : `${date}, ${time}`;
}

function ago(value) {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return then.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/**
 * A photo renders as a tappable thumbnail instead of a bare download chip —
 * fetched as a blob through the api client (not a plain <img src>) so the
 * request still carries the session cookie and, for an owner viewing
 * another tenant's conversation, the X-Tenant-Id header, neither of which a
 * bare <img> tag would send. Non-image attachments still use the chip below.
 */
function AttachmentImage({ att, onOpen }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    api
      .get(att.download_path, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [att.download_path]);

  if (failed) {
    // Falls back to a plain download chip so a broken fetch still lets the
    // file through rather than showing a dead thumbnail.
    return (
      <a
        href={att.download_path}
        className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[12px] text-ink hover:border-brand-ring transition-colors max-w-full shadow-card"
        title={`Download ${att.filename}`}
      >
        <FileIcon className="w-3.5 h-3.5 text-muted2 shrink-0" />
        <span className="truncate max-w-[180px]">{att.filename}</span>
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => url && onOpen(url, att.filename)}
      className="block w-[200px] h-[160px] rounded-xl overflow-hidden border border-line shadow-card bg-muted"
      title={att.filename}
    >
      {url ? (
        <img src={url} alt={att.filename} className="block w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted2">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}
    </button>
  );
}

function bytesToSize(n) {
  if (!n) return "0 KB";
  const kb = n / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** Local calendar day of an ISO timestamp — for grouping messages by day. */
function dayKey(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toDateString();
}

/** "Today", "Yesterday", "Friday, Oct 30" — or with the year if it isn't this one. */
function dayLabel(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
}

/** mm:ss — voice notes never run long enough to need an hours place. */
function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

const WAVE_BARS = 36;
const LIVE_BARS = 40;
const MIN_VOICE_NOTE_MS = 1000;
const COMPOSER_MAX_LINES = 6;
const PAGE_SIZE = 50; // messages per history page

/**
 * Decode an audio URL once and boil it down to `bars` loudness peaks (0–1)
 * for the waveform. Uses an OfflineAudioContext, which needs no user gesture
 * and holds no audio device open. Also returns the decoded duration — a
 * MediaRecorder webm has no duration in its header (Chrome reports Infinity
 * until it's played through), so this is what makes the timer correct.
 * Anything that fails to decode just gets a flat placeholder wave.
 */
function useWaveform(src, bars = WAVE_BARS) {
  const [peaks, setPeaks] = useState(null);
  const [decodedDuration, setDecodedDuration] = useState(0);

  useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;
    const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!Offline) return undefined;
    (async () => {
      try {
        const buf = await (await fetch(src)).arrayBuffer();
        const ctx = new Offline(1, 1, 44100);
        const audio = await new Promise((resolve, reject) => {
          const p = ctx.decodeAudioData(buf, resolve, reject);
          if (p && typeof p.then === "function") p.then(resolve, reject);
        });
        const data = audio.getChannelData(0);
        const block = Math.max(1, Math.floor(data.length / bars));
        const out = [];
        for (let i = 0; i < bars; i++) {
          let sum = 0;
          const start = i * block;
          const end = Math.min(start + block, data.length);
          for (let j = start; j < end; j++) sum += data[j] * data[j];
          out.push(Math.sqrt(sum / Math.max(1, end - start)));
        }
        const max = Math.max(...out) || 1;
        if (!cancelled) {
          setPeaks(out.map((v) => Math.max(0.1, v / max)));
          setDecodedDuration(audio.duration || 0);
        }
      } catch {
        /* leave the placeholder wave */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src, bars]);

  return { peaks, decodedDuration };
}

// Gentle, fixed shape shown while decoding (or if decoding isn't possible).
const PLACEHOLDER_PEAKS = Array.from({ length: WAVE_BARS }, (_, i) => 0.25 + 0.2 * Math.abs(Math.sin(i * 0.9)));

/**
 * A voice-note clip: round play button, a waveform that fills in as it
 * plays (click or arrow-key to seek), and the time. Used both for a voice
 * note waiting to be sent (an object URL over the local File) and for one
 * already on a message (a blob URL fetched through the api client, see
 * AttachmentAudio below). `mine` flips it to the sent-bubble (brand-filled)
 * treatment, same as a text bubble.
 */
function AudioPlayer({ src, mine }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [metaDuration, setMetaDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const { peaks, decodedDuration } = useWaveform(src);
  const duration = Number.isFinite(metaDuration) && metaDuration > 0 ? metaDuration : decodedDuration;
  const progress = duration ? Math.min(1, current / duration) : 0;
  const bars = peaks || PLACEHOLDER_PEAKS;

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause();
    else a.play().catch(() => {});
  };

  const seekTo = (t) => {
    if (!duration) return;
    const v = Math.max(0, Math.min(duration, t));
    if (audioRef.current) audioRef.current.currentTime = v;
    setCurrent(v);
  };

  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-xl pl-1.5 pr-3 py-1.5 w-[240px] shadow-card",
        mine ? "bg-brand text-white" : "bg-surface border border-line text-ink"
      )}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
        }}
        onLoadedMetadata={(e) => setMetaDuration(e.currentTarget.duration || 0)}
        onDurationChange={(e) => setMetaDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime || 0)}
        className="hidden"
      />
      <button
        type="button"
        onClick={toggle}
        className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors",
          mine ? "bg-white/25 hover:bg-white/35 text-white" : "bg-ink text-card hover:bg-ink/85"
        )}
        aria-label={playing ? "Pause" : "Play"}
        title={playing ? "Pause" : "Play"}
      >
        {playing ? (
          <Pause className="w-3.5 h-3.5" fill="currentColor" />
        ) : (
          <Play className="w-3.5 h-3.5 ml-0.5" fill="currentColor" />
        )}
      </button>
      <div
        role="slider"
        tabIndex={0}
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration || 0)}
        aria-valuenow={Math.round(current)}
        aria-valuetext={formatDuration(current)}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          seekTo(((e.clientX - r.left) / r.width) * duration);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") seekTo(current + 2);
          else if (e.key === "ArrowLeft") seekTo(current - 2);
          else return;
          e.preventDefault();
        }}
        className="flex-1 flex items-center gap-[2px] h-7 cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-ring"
      >
        {bars.map((p, i) => (
          <span
            key={i}
            className={cn(
              "flex-1 rounded-full transition-colors",
              (i + 0.5) / bars.length <= progress
                ? mine
                  ? "bg-white"
                  : "bg-ink"
                : mine
                ? "bg-white/45"
                : "bg-ink/25"
            )}
            style={{ height: `${Math.round(p * 100)}%`, minHeight: 3 }}
          />
        ))}
      </div>
      <span
        className={cn("text-[10.5px] font-mono tabular-nums shrink-0 w-8 text-right", mine ? "text-white/85" : "text-muted2")}
      >
        {formatDuration(playing || current ? current : duration)}
      </span>
    </div>
  );
}

/**
 * A voice note (or any audio file) attached to a sent message — fetched as a
 * blob the same way AttachmentImage fetches a photo, so the request still
 * carries the session cookie and X-Tenant-Id header.
 */
function AttachmentAudio({ att, mine }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    api
      .get(att.download_path, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [att.download_path]);

  if (failed) {
    return (
      <a
        href={att.download_path}
        className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[12px] text-ink hover:border-brand-ring transition-colors max-w-full shadow-card"
        title={`Download ${att.filename}`}
      >
        <FileIcon className="w-3.5 h-3.5 text-muted2 shrink-0" />
        <span className="truncate max-w-[180px]">{att.filename}</span>
      </a>
    );
  }

  if (!url) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-xl border w-[240px] h-[44px] shrink-0",
          mine ? "bg-brand/80 border-transparent text-white" : "bg-surface border-line text-muted2"
        )}
      >
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  return <AudioPlayer src={url} mine={mine} />;
}

/** Direct: the other person's name. Group: its title, or everyone's names.
 * An admin looking in on a conversation they're not part of (Team inbox) has
 * no "you" to anchor on — every participant is "someone else" — so a direct
 * conversation there names both people instead of silently dropping one. */
function conversationLabel(c) {
  const others = (c.participants || []).filter((p) => !p.you);
  if (c.type === "group") {
    if (c.title) return c.title;
    return others.map((p) => p.name).join(", ") || "Just you";
  }
  if (c.is_participant === false) {
    return others.map((p) => p.name).join(" & ") || "Unknown";
  }
  return others[0]?.name || "Unknown";
}

function conversationInitial(c) {
  const label = conversationLabel(c);
  return (label[0] || "?").toUpperCase();
}

/** The other person in a direct conversation — undefined for a group. */
function otherParticipant(c) {
  if (c.type === "group") return undefined;
  return (c.participants || []).find((p) => !p.you);
}

/** The @-mention being typed right before the caret, or null if there isn't
 * one — "@" must start at a word boundary and have no whitespace after it
 * yet. Returns the partial name typed so far (possibly ""). */
function mentionQueryAt(value, caret) {
  const uptoCaret = value.slice(0, caret);
  const match = uptoCaret.match(/(?:^|\s)@([^\s@]*)$/);
  return match ? match[1] : null;
}

/** Highlights literal "@Name" runs in a message body for each mentioned
 * user_id, looking their current name up from the conversation's
 * participants. A mention whose name can no longer be found (renamed since,
 * or a data mismatch) is just left as plain text rather than breaking. */
function renderMessageBody(body, mentionIds, participants) {
  if (!body) return body;
  if (!mentionIds || mentionIds.length === 0) return body;
  let parts = [{ text: body, mention: false }];
  for (const uid of mentionIds) {
    const name = (participants || []).find((p) => p.user_id === uid)?.name;
    if (!name) continue;
    const needle = `@${name}`;
    const next = [];
    for (const part of parts) {
      if (part.mention) {
        next.push(part);
        continue;
      }
      const idx = part.text.indexOf(needle);
      if (idx === -1) {
        next.push(part);
        continue;
      }
      if (idx > 0) next.push({ text: part.text.slice(0, idx), mention: false });
      next.push({ text: part.text.slice(idx, idx + needle.length), mention: true });
      const rest = part.text.slice(idx + needle.length);
      if (rest) next.push({ text: rest, mention: false });
    }
    parts = next;
  }
  return parts.map((p, i) =>
    p.mention ? (
      <span key={i} className="text-brand font-semibold">
        {p.text}
      </span>
    ) : (
      <span key={i}>{p.text}</span>
    )
  );
}

function OnlineDot({ show, className }) {
  if (!show) return null;
  return (
    <span
      className={cn(
        "absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-card",
        className
      )}
      title="Online"
    />
  );
}

export default function Messages() {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const confirm = useConfirm();
  const CK = cacheKey("messages", tenantId);
  const cached = cacheGet(CK);
  const [searchParams, setSearchParams] = useSearchParams();
  // Seeded from cache so revisiting shows the conversation list instantly
  // instead of flashing a loading skeleton — see lib/cache.js.
  const [conversations, setConversations] = useState(() => cached || []);
  const [loading, setLoading] = useState(() => !cached);
  const showLoading = useDelayedLoading(loading);
  // A link from Dashboard or search (?c=<id>) picks a conversation before the
  // list has even loaded — honored once, then cleared from the URL.
  const [activeId, setActiveId] = useState(() => searchParams.get("c") || null);
  // A search result can also name a specific message (?m=) — read once at
  // mount, same as ?c=, since the deep-link effect below clears both from
  // the URL once ?c= has been applied.
  const [pendingMessageId, setPendingMessageId] = useState(() => searchParams.get("m") || null);
  const [highlightTarget, setHighlightTarget] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const deepLinkApplied = useRef(false);
  const [messages, setMessages] = useState([]);
  // Mirrors `messages` for code that needs to read the current list
  // synchronously (e.g. right after calling setMessages, in the same tick) —
  // React doesn't guarantee a functional setState updater has actually run
  // by the time the call that triggered it returns, so reading state back
  // out of the updater itself isn't safe. Same pattern as activeIdRef below.
  const messagesRef = useRef([]);
  messagesRef.current = messages;
  const [threadLoading, setThreadLoading] = useState(false);
  // ---- History paging: the thread opens on the latest PAGE_SIZE messages;
  // scrolling to the top pulls the page before that, and so on back to the
  // very first message (where a "start of your conversation" marker shows).
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);
  // After jumping to an old message (search / pinned), what's loaded is a
  // window in the middle of history, not the latest page — polls then fetch
  // everything *after* it instead, so no gap opens up between the two.
  const detachedRef = useRef(false);
  const loadSeqRef = useRef(0); // bumps on every initial load, so late answers to older requests are ignored
  const initialInFlightRef = useRef(false);
  const pendingPrependRef = useRef(null); // scroll position to restore after older messages land
  const nearBottomRef = useRef(true);
  const scrollToBottomRef = useRef(false); // set by an initial load
  const justSentRef = useRef(false); // you just sent something — follow it to the bottom
  const [showJump, setShowJump] = useState(false);
  const [text, setText] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef(null);
  const threadRef = useRef(null);
  const first = useRef(true);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [busyMessageId, setBusyMessageId] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const lastTypingSentRef = useRef(0);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [mentionIds, setMentionIds] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const textInputRef = useRef(null);
  // Admins and the owner can look in on every conversation in the business,
  // not just their own — off by default so an admin's own inbox doesn't
  // silently turn into the whole team's the moment they open Messages.
  const canViewAll = ["owner", "admin"].includes(user?.role);
  const [viewAll, setViewAll] = useState(false);

  const loadConversations = useCallback(
    async (selectId) => {
      try {
        const { data } = await api.get("/conversations", { params: viewAll ? { all: true } : undefined });
        setConversations(data);
        cacheSet(CK, data);
        if (selectId !== undefined) {
          setActiveId(selectId);
        } else {
          setActiveId((current) => current || data[0]?.conversation_id || null);
        }
      } catch (err) {
        if (first.current) toast.error(apiError(err));
      } finally {
        first.current = false;
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [CK, viewAll]
  );

  const loadMessages = useCallback(async (conversationId, { showSpinner, around, initial } = {}) => {
    if (!conversationId) return;
    if (showSpinner) setThreadLoading(true);
    try {
      // A background poll of a thread that's been jumped into the middle of
      // fetches only what came after the loaded window (closing any gap);
      // otherwise it's the latest page, merged in so older pages already
      // scrolled back to aren't thrown away.
      //
      // Background refreshes never race a conversation's initial load: one
      // that starts while an initial load is in flight is skipped, and one
      // whose answer lands after a newer initial load is thrown away —
      // otherwise a stale "latest page" could overwrite a jump-to-message.
      if (!initial && initialInFlightRef.current) return;
      const seq = initial ? ++loadSeqRef.current : loadSeqRef.current;
      if (initial) initialInFlightRef.current = true;
      const before = messagesRef.current;
      const catchUp =
        !initial && !around && detachedRef.current && before.length > 0 && before[0].conversation_id === conversationId;
      const params = around
        ? { around }
        : catchUp
        ? { after: before[before.length - 1].created_at }
        : { limit: PAGE_SIZE };
      let page;
      try {
        ({ data: page } = await api.get(`/conversations/${conversationId}/messages`, { params }));
      } finally {
        if (initial && seq === loadSeqRef.current) initialInFlightRef.current = false;
      }
      const stale =
        seq !== loadSeqRef.current || (!initial && (initialInFlightRef.current || catchUp !== detachedRef.current));
      // A poll firing after the user has already switched threads shouldn't
      // paint the wrong conversation's messages into view.
      if (activeIdRef.current === conversationId && !stale) {
        // Read the list as it is *now*, not as it was when the request left —
        // older pages may have been prepended while it was in flight.
        const existing = messagesRef.current;
        let data;
        if (initial) {
          data = page;
          // A full page means there may be more above; a jump window always
          // might. Fetching older returns fewer than a page once it hits the start.
          setHasOlder(around ? page.length > 0 : page.length >= PAGE_SIZE);
          detachedRef.current = !!around;
          // A jump lands mid-history: don't treat the view as "at the bottom",
          // or the first background refresh would drag it down to the newest.
          nearBottomRef.current = !around;
          if (!around) scrollToBottomRef.current = true;
        } else if (catchUp) {
          const seen = new Set(existing.map((m) => m.message_id));
          data = [...existing, ...page.filter((m) => !seen.has(m.message_id))];
          if (page.length < 500) detachedRef.current = false;
        } else if (page.length === 0) {
          data = [];
        } else {
          const firstAt = page[0].created_at;
          const older = existing.filter(
            (m) => m.conversation_id === conversationId && m.created_at < firstAt
          );
          data = [...older, ...page];
        }
        // Diff against the ref, not React state, and do it before calling
        // setMessages — a functional setState updater isn't guaranteed to
        // run synchronously, so anything read back immediately after
        // calling setMessages could still see the old value.
        //
        // `initial` marks the very first load of a conversation (opening it,
        // switching into it, or jumping to a message inside it) — at that
        // point messagesRef.current still holds whatever the *previous*
        // conversation had (or is empty), so every message here would
        // otherwise look "arrived" and the sound would fire on every single
        // click into a thread instead of only on real new messages. Only
        // the background polls/pushes that recheck an already-open thread
        // should ever treat a message as newly arrived.
        const priorIds = new Set(messagesRef.current.map((m) => m.message_id));
        const arrived = initial ? [] : data.filter((m) => !priorIds.has(m.message_id));
        setMessages(data);
        messagesRef.current = data;
        if (arrived.length) {
          const arrivedSenderIds = arrived.map((m) => m.sender_id);
          // Whoever just sent one of these obviously isn't still typing it —
          // drop them from the indicator right away instead of leaving it
          // up to the typing poll's own ~2.5s cycle to notice.
          setTypingUsers((cur) => cur.filter((t) => !arrivedSenderIds.includes(t.user_id)));
        }
        const newFromSomeoneElse = arrived.some((m) => m.sender_id !== user?.user_id);
        // A message from someone else landed while this thread was already
        // open — play the sound right here. The sidebar's own unread-count
        // poll is the usual trigger for that (see Layout.jsx), but marking
        // this read immediately below means the count may round-trip back
        // to 0 before that poll ever samples the bump, so this thread has
        // to raise it directly instead of relying on the count ever changing.
        if (newFromSomeoneElse) playSelectedSound();
        // A message from someone else landed while this thread was already
        // open — mark it read again right away. Without this, only the
        // /read call on first opening a conversation ever fires, so a
        // message that arrives while you're already looking at it stays
        // "unread" on the bell/sidebar until you switch away and back.
        if (newFromSomeoneElse) {
          api
            .post(`/conversations/${conversationId}/read`)
            .then(() => emitChange(MESSAGES_CHANGED))
            .catch(() => {});
        }
      }
    } catch (err) {
      if (activeIdRef.current === conversationId) toast.error(apiError(err));
    } finally {
      if (showSpinner) setThreadLoading(false);
    }
  }, [user]);

  // One page further back through history, prepended without moving what's
  // on screen (the layout effect below restores the scroll offset).
  const loadOlder = useCallback(async () => {
    const conversationId = activeIdRef.current;
    const oldest = messagesRef.current[0];
    if (!conversationId || !oldest || loadingOlderRef.current || initialInFlightRef.current) return;
    const seq = loadSeqRef.current;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const { data } = await api.get(`/conversations/${conversationId}/messages`, {
        params: { before: oldest.created_at, limit: PAGE_SIZE },
      });
      if (activeIdRef.current !== conversationId || seq !== loadSeqRef.current) return;
      if (data.length < PAGE_SIZE) setHasOlder(false);
      if (data.length) {
        const el = threadRef.current;
        if (el) pendingPrependRef.current = { height: el.scrollHeight, top: el.scrollTop };
        const seen = new Set(messagesRef.current.map((m) => m.message_id));
        const merged = [...data.filter((m) => !seen.has(m.message_id)), ...messagesRef.current];
        messagesRef.current = merged;
        setMessages(merged);
      }
    } catch (err) {
      if (activeIdRef.current === conversationId) toast.error(apiError(err));
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, []);

  // Loads a conversation's messages centered on one message_id (from search,
  // or the pinned-messages panel), then scrolls it into view and briefly
  // flashes it once it's rendered — see the effect below.
  const jumpToMessage = useCallback(
    async (conversationId, messageId) => {
      if (!conversationId || !messageId) return;
      await loadMessages(conversationId, { showSpinner: true, around: messageId, initial: true });
      setHighlightTarget(messageId);
    },
    [loadMessages]
  );

  // Once the targeted message is actually in the DOM, scroll to it and flash
  // its background for ~1.5s.
  useEffect(() => {
    if (!highlightTarget) return;
    const id = highlightTarget;
    const el = document.getElementById(`msg-${id}`);
    if (!el) return;
    el.scrollIntoView({ block: "center" });
    setHighlightedId(id);
    setHighlightTarget(null);
    const timer = setTimeout(() => {
      setHighlightedId((cur) => (cur === id ? null : cur));
    }, 1500);
    return () => clearTimeout(timer);
  }, [messages, highlightTarget]);

  const loadPinned = useCallback(async (conversationId) => {
    if (!conversationId) {
      setPinnedMessages([]);
      return;
    }
    try {
      const { data } = await api.get(`/conversations/${conversationId}/pinned`);
      if (activeIdRef.current === conversationId) setPinnedMessages(data.pinned || []);
    } catch {
      // Quiet — a secondary panel not loading isn't worth a toast.
    }
  }, []);

  useEffect(() => {
    loadPinned(activeId);
  }, [activeId, loadPinned]);

  // Conversation list — quiet background refresh, no spinner, no lost scroll.
  useEffect(() => {
    loadConversations();
    const timer = setInterval(() => loadConversations(), POLL_MS);
    const onFocus = () => loadConversations();
    window.addEventListener("focus", onFocus);
    const off = onChange(MESSAGES_CHANGED, () => loadConversations());
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      off();
    };
  }, [loadConversations]);

  // Active thread — its own poll, and marked read whenever it changes.
  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    if (pendingMessageId) {
      // Consume it immediately — it's a one-time jump for this activation,
      // not something later polls or re-renders should repeat.
      const mid = pendingMessageId;
      setPendingMessageId(null);
      jumpToMessage(activeId, mid);
    } else {
      loadMessages(activeId, { showSpinner: true, initial: true });
    }
    api
      .post(`/conversations/${activeId}/read`)
      .then(() => emitChange(MESSAGES_CHANGED))
      .catch(() => {});
    const timer = setInterval(() => loadMessages(activeId), POLL_MS);
    // A push notification arriving for this conversation re-fires this too —
    // catches a reply while the thread's already open, without waiting on
    // the interval above at all.
    const off = onChange(MESSAGES_CHANGED, () => loadMessages(activeId));
    return () => {
      clearInterval(timer);
      off();
    };
    // pendingMessageId is read once above and cleared in the same tick —
    // it's deliberately not a dependency, since including it would re-run
    // this effect (and re-jump) exactly when we clear it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, loadMessages, jumpToMessage]);

  // Where the thread should sit after its messages change:
  //  - older page prepended → keep the same message under your eyes
  //  - conversation just opened → the newest message
  //  - new message at the bottom → follow it if you were already at the
  //    bottom (or sent it yourself); otherwise stay put while you read back
  const lastMessageIdRef = useRef(null);
  useLayoutEffect(() => {
    const el = threadRef.current;
    const last = messages[messages.length - 1];
    const lastChanged = (last?.message_id || null) !== lastMessageIdRef.current;
    lastMessageIdRef.current = last?.message_id || null;
    if (!el) return;
    if (pendingPrependRef.current) {
      const { height, top } = pendingPrependRef.current;
      pendingPrependRef.current = null;
      el.scrollTop = top + (el.scrollHeight - height);
      return;
    }
    if (highlightTarget) return; // a jump load handles its own scrolling
    if (scrollToBottomRef.current) {
      scrollToBottomRef.current = false;
      el.scrollTop = el.scrollHeight;
      nearBottomRef.current = true;
      return;
    }
    // Only a message you *just* sent pulls the view down — not an older one
    // of yours that a background catch-up happens to bring in.
    const followMine = justSentRef.current && last?.sender_id === user?.user_id;
    if (lastChanged && (nearBottomRef.current || followMine)) {
      el.scrollTop = el.scrollHeight;
    }
    if (lastChanged && followMine) justSentRef.current = false;
  }, [messages, highlightTarget, user]);

  // A first page too short to fill the screen leaves nothing to scroll, so
  // the scroll-to-top trigger could never fire — keep loading until it can.
  useEffect(() => {
    const el = threadRef.current;
    if (!el || !hasOlder || loadingOlder || !messages.length || highlightTarget) return;
    if (el.scrollHeight <= el.clientHeight + 40) loadOlder();
  }, [messages, hasOlder, loadingOlder, highlightTarget, loadOlder]);

  const onThreadScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    nearBottomRef.current = fromBottom < 120;
    setShowJump(fromBottom > 600 || detachedRef.current);
    if (el.scrollTop < 80 && hasOlder) loadOlder();
  };

  const jumpToLatest = () => {
    if (detachedRef.current && activeId) {
      // The loaded window doesn't reach the present yet — reload the latest page.
      loadMessages(activeId, { initial: true });
    } else if (threadRef.current) {
      const el = threadRef.current;
      const far = el.scrollHeight - el.scrollTop - el.clientHeight > 2500;
      // Far back, a smooth scroll would crawl through every message — just go.
      el.scrollTo({ top: el.scrollHeight, behavior: far ? "auto" : "smooth" });
      nearBottomRef.current = true;
    }
    setShowJump(false);
  };

  // Who else is typing in the active thread — polled on its own, faster
  // cadence than the message poll since it's meant to feel live.
  useEffect(() => {
    if (!activeId) {
      setTypingUsers([]);
      return;
    }
    let cancelled = false;
    const check = () =>
      api
        .get(`/conversations/${activeId}/typing`)
        .then(({ data }) => !cancelled && setTypingUsers(data.typing || []))
        .catch(() => {});
    check();
    const timer = setInterval(check, 2500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeId]);

  // Once the list has loaded, drop ?c= from the URL (it's done its job) and
  // fall back to the first conversation if it named one that doesn't exist.
  useEffect(() => {
    if (loading || deepLinkApplied.current) return;
    const wanted = searchParams.get("c");
    if (wanted) {
      deepLinkApplied.current = true;
      if (!conversations.some((c) => c.conversation_id === wanted)) {
        setActiveId(conversations[0]?.conversation_id || null);
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("c");
        next.delete("m");
        return next;
      }, { replace: true });
    }
  }, [loading, conversations, searchParams, setSearchParams]);

  const active = conversations.find((c) => c.conversation_id === activeId) || null;
  const unreadTotal = conversations.filter((c) => c.unread).length;
  // False only when an admin/owner has opened a conversation they aren't
  // actually part of via "Team inbox" — the backend enforces this too
  // (conversation_visible vs. conversation_member), this just keeps the UI
  // from offering actions that would 404.
  const isParticipant = active ? active.is_participant !== false : true;

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setPendingFiles((cur) => {
      const merged = [...cur, ...incoming];
      if (merged.length > MAX_FILES) {
        toast.error(`Attach at most ${MAX_FILES} files at once`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };

  const removeFile = (idx) => setPendingFiles((cur) => cur.filter((_, i) => i !== idx));

  // Preview URLs for any pending audio file — a fresh recording or a picked
  // voice memo alike — so it can be played back before sending. Rebuilt
  // whenever the pending list changes, and the old ones revoked right after
  // so a removed or sent file's object URL doesn't leak.
  const pendingAudioUrls = useMemo(
    () => pendingFiles.map((f) => (f.type?.startsWith("audio/") ? URL.createObjectURL(f) : null)),
    [pendingFiles]
  );
  useEffect(() => {
    return () => {
      pendingAudioUrls.forEach((u) => u && URL.revokeObjectURL(u));
    };
  }, [pendingAudioUrls]);

  // ---- Voice notes: record in-browser, then hand the result to the same
  // pendingFiles/send() path a picked file already goes through — no
  // separate send pathway needed, and it means a caption can still be typed
  // alongside the recording before it goes out. ----
  const micSupported = typeof window !== "undefined" && !!(navigator.mediaDevices && window.MediaRecorder);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [recordPaused, setRecordPaused] = useState(false);
  const [liveLevels, setLiveLevels] = useState(() => Array(LIVE_BARS).fill(0));
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordStreamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const recordPausedRef = useRef(false);
  const recordAccumMsRef = useRef(0); // recorded time banked before the last pause
  const recordSegStartRef = useRef(0); // when the current (unpaused) stretch began
  const meterRef = useRef(null); // { ctx, raf } for the live waveform

  // Live waveform: an AnalyserNode on the mic stream, sampled ~12×/s into a
  // rolling strip of bars (newest on the right). Paused = no new samples, so
  // the wave freezes where it was. Purely visual — it never touches what
  // MediaRecorder captures.
  const startMeter = (stream) => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    try {
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      let last = 0;
      const tick = (now) => {
        meterRef.current.raf = requestAnimationFrame(tick);
        if (recordPausedRef.current || now - last < 80) return;
        last = now;
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        // RMS of speech is small; scale and soften so normal talking fills the bar.
        const level = Math.min(1, Math.sqrt(Math.sqrt(sum / buf.length)) * 1.6);
        setLiveLevels((cur) => [...cur.slice(1), level]);
      };
      meterRef.current = { ctx, raf: requestAnimationFrame(tick) };
    } catch {
      meterRef.current = null;
    }
  };

  const stopMeter = () => {
    const m = meterRef.current;
    if (!m) return;
    cancelAnimationFrame(m.raf);
    m.ctx.close?.().catch?.(() => {});
    meterRef.current = null;
  };

  const canPauseRecording = typeof window !== "undefined" && typeof window.MediaRecorder?.prototype?.pause === "function";

  const pauseRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || r.state !== "recording") return;
    r.pause();
    recordAccumMsRef.current += performance.now() - recordSegStartRef.current;
    recordPausedRef.current = true;
    setRecordPaused(true);
  };

  const resumeRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || r.state !== "paused") return;
    r.resume();
    recordSegStartRef.current = performance.now();
    recordPausedRef.current = false;
    setRecordPaused(false);
  };

  const pickAudioMimeType = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
    for (const t of candidates) {
      if (window.MediaRecorder?.isTypeSupported?.(t)) return t;
    }
    return ""; // let the browser fall back to its own default
  };

  const startRecording = async () => {
    if (!micSupported || recording || sending) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const mimeType = pickAudioMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recordedChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      // stopRecording() always swaps in its own onstop (queue / send / discard).
      mediaRecorderRef.current = recorder;
      recorder.start();
      recordAccumMsRef.current = 0;
      recordSegStartRef.current = performance.now();
      recordPausedRef.current = false;
      setRecordPaused(false);
      setLiveLevels(Array(LIVE_BARS).fill(0));
      setRecording(true);
      setRecordSeconds(0);
      // The clock holds while paused, so it always matches the clip's length.
      recordTimerRef.current = setInterval(() => {
        if (!recordPausedRef.current) setRecordSeconds((s) => s + 1);
      }, 1000);
      startMeter(stream);
    } catch {
      toast.error("Couldn't access your microphone — check this site's permission in your browser.");
    }
  };

  // Three ways out of a recording:
  //   discard          → throw the take away, just release the mic
  //   (default) queue  → park the clip in the composer to review / caption
  //   sendNow          → send it straight away, together with any text or
  //                      files already in the composer
  // Takes under MIN_VOICE_NOTE_MS of actual recording (pauses excluded) are
  // dropped either way, so a stray tap never sends or queues an empty clip.
  const stopRecording = ({ discard, sendNow } = {}) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    const elapsedMs =
      recordAccumMsRef.current + (recordPausedRef.current ? 0 : performance.now() - recordSegStartRef.current);
    const tooShort = !discard && elapsedMs < MIN_VOICE_NOTE_MS;
    if (tooShort) toast("Voice note too short — hold on a little longer.");
    if (sendNow && !tooShort && pendingFiles.length >= MAX_FILES) {
      toast.error(`Attach at most ${MAX_FILES} files at once — your voice note was kept below instead.`);
      sendNow = false;
    }
    const releaseMic = () => {
      recordStreamRef.current?.getTracks().forEach((t) => t.stop());
      recordStreamRef.current = null;
    };
    if (discard || tooShort) {
      recorder.ondataavailable = null;
      recorder.onstop = releaseMic;
    } else {
      recorder.onstop = () => {
        releaseMic();
        const type = recorder.mimeType || "audio/webm";
        const blob = new Blob(recordedChunksRef.current, { type });
        if (blob.size === 0) return;
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `Voice message.${ext}`, { type });
        if (sendNow) send([file]);
        else addFiles([file]);
      };
    }
    recorder.stop(); // valid from "paused" too — the paused take is kept
    mediaRecorderRef.current = null;
    setRecording(false);
    recordPausedRef.current = false;
    setRecordPaused(false);
    clearInterval(recordTimerRef.current);
    recordTimerRef.current = null;
    stopMeter();
  };

  // Navigating away mid-recording shouldn't leave the mic indicator lit or a
  // timer ticking in the background.
  useEffect(
    () => () => {
      clearInterval(recordTimerRef.current);
      stopMeter();
      recordStreamRef.current?.getTracks().forEach((t) => t.stop());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Debounced — at most one /typing ping every ~3s while the composer has
  // text in it and is still changing, not one per keystroke. Also tracks
  // whether the caret is mid-@mention, to drive the mention popover below.
  const handleTextChange = (e) => {
    const value = e.target.value;
    const caret = e.target.selectionStart ?? value.length;
    setText(value);
    setMentionQuery(mentionQueryAt(value, caret));
    if (!value) setMentionIds([]);
    if (!active || !value) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 3000) return;
    lastTypingSentRef.current = now;
    api.post(`/conversations/${active.conversation_id}/typing`).catch(() => {});
  };

  // Candidates for the @mention popover — this conversation's other
  // participants (not the full team) since only they can actually be
  // recorded as a mention (send_message drops anyone else server-side).
  const mentionCandidates = useMemo(() => {
    if (!active || mentionQuery === null) return [];
    const term = mentionQuery.toLowerCase();
    return (active.participants || [])
      .filter((p) => !p.you && p.name.toLowerCase().includes(term))
      .slice(0, 8);
  }, [active, mentionQuery]);

  // The message box grows with its content up to COMPOSER_MAX_LINES, then
  // scrolls inside itself; it shrinks back to one line once sent/cleared.
  // Re-runs when the box remounts (it's swapped out while recording) or the
  // conversation changes.
  useLayoutEffect(() => {
    const el = textInputRef.current;
    if (!el) return;
    const cs = window.getComputedStyle(el);
    const lineH = parseFloat(cs.lineHeight) || 20;
    const chrome =
      parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const max = lineH * COMPOSER_MAX_LINES + chrome;
    el.style.height = "auto";
    const needed = el.scrollHeight + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    el.style.height = `${Math.min(needed, max)}px`;
    el.style.overflowY = needed > max ? "auto" : "hidden";
  }, [text, recording, activeId]);

  const insertMention = (participant) => {
    const el = textInputRef.current;
    const caret = el ? el.selectionStart : text.length;
    const uptoCaret = text.slice(0, caret);
    const atIdx = uptoCaret.lastIndexOf("@");
    if (atIdx === -1) return;
    const before = text.slice(0, atIdx);
    const after = text.slice(caret);
    const insertion = `@${participant.name} `;
    setText(before + insertion + after);
    setMentionIds((cur) => (cur.includes(participant.user_id) ? cur : [...cur, participant.user_id]));
    setMentionQuery(null);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = before.length + insertion.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const [emojiOpen, setEmojiOpen] = useState(false);
  // Which picker the composer's popover is currently showing — the button
  // and popover are shared, only the content inside switches.
  const [pickerMode, setPickerMode] = useState("emoji");

  const insertEmoji = (emoji) => {
    const el = textInputRef.current;
    const caret = el ? el.selectionStart ?? text.length : text.length;
    const before = text.slice(0, caret);
    const after = text.slice(caret);
    setText(before + emoji + after);
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = before.length + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // The message you've tapped "Reply" on — shown as a preview strip above
  // the composer, like most chat apps, until you send (or cancel it).
  const [replyingTo, setReplyingTo] = useState(null);
  const cancelReply = () => setReplyingTo(null);
  const startReply = (m) => {
    setReplyingTo(m);
    textInputRef.current?.focus();
  };

  // `extra` = files to send alongside whatever is already queued (the
  // recorder's send-now button passes the fresh voice note this way). Called
  // as an onClick handler it receives an event instead, hence the guard.
  const send = async (extra) => {
    const extraFiles = Array.isArray(extra) ? extra : [];
    const files = [...pendingFiles, ...extraFiles];
    if (!active || (!text.trim() && files.length === 0)) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("body", text.trim());
      form.append("mentions", JSON.stringify(mentionIds));
      if (replyingTo) form.append("reply_to", replyingTo.message_id);
      files.forEach((f) => form.append("files", f));
      await api.post(`/conversations/${active.conversation_id}/messages`, form);
      justSentRef.current = true;
      setText("");
      setMentionIds([]);
      setMentionQuery(null);
      setPendingFiles([]);
      setReplyingTo(null);
      await Promise.all([loadMessages(active.conversation_id), loadConversations(active.conversation_id)]);
      emitChange(MESSAGES_CHANGED);
    } catch (err) {
      toast.error(apiError(err));
      // Don't lose a voice note that failed to go out — park it in the
      // composer so it can be retried with the normal send button.
      if (extraFiles.length) addFiles(extraFiles);
    } finally {
      setSending(false);
    }
  };

  // Stickers send the moment you tap one — no typing, no separate "send"
  // step, the same as WhatsApp/Telegram/iMessage. Mirrors send()'s
  // FormData/refresh pattern but posts the sticker PNG as the sole
  // attachment with an empty body, and still honors an in-progress reply.
  const sendSticker = async (sticker) => {
    if (!active || sending) return;
    setSending(true);
    try {
      const res = await fetch(`/stickers/${sticker.file}`);
      const blob = await res.blob();
      const file = new File([blob], sticker.file, { type: blob.type || "image/png" });
      const form = new FormData();
      form.append("body", "");
      form.append("mentions", "[]");
      if (replyingTo) form.append("reply_to", replyingTo.message_id);
      form.append("files", file);
      await api.post(`/conversations/${active.conversation_id}/messages`, form);
      justSentRef.current = true;
      setReplyingTo(null);
      setEmojiOpen(false);
      await Promise.all([loadMessages(active.conversation_id), loadConversations(active.conversation_id)]);
      emitChange(MESSAGES_CHANGED);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  // Shared by react/edit/delete — the conversation preview may have changed
  // (last message text, or which message is last), so the list refreshes too.
  const refreshAfterAction = async () => {
    if (!activeId) return;
    await Promise.all([loadMessages(activeId), loadConversations(activeId), loadPinned(activeId)]);
    emitChange(MESSAGES_CHANGED);
  };

  const toggleReaction = async (messageId, emoji) => {
    if (!active) return;
    try {
      await api.post(`/conversations/${active.conversation_id}/messages/${messageId}/reactions`, {
        emoji,
      });
      await refreshAfterAction();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const togglePin = async (messageId) => {
    if (!active) return;
    try {
      await api.post(`/conversations/${active.conversation_id}/messages/${messageId}/pin`);
      await refreshAfterAction();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const startEdit = (m) => {
    setEditingId(m.message_id);
    setEditText(m.body || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const saveEdit = async (messageId) => {
    if (!active) return;
    const text = editText.trim();
    if (!text) return;
    setBusyMessageId(messageId);
    try {
      await api.put(`/conversations/${active.conversation_id}/messages/${messageId}`, { text });
      setEditingId(null);
      setEditText("");
      await refreshAfterAction();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyMessageId(null);
    }
  };

  const deleteMessage = async (m) => {
    if (!active) return;
    const ok = await confirm({
      title: "Delete this message?",
      body: "This can't be undone. Any attached files will be removed too.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setBusyMessageId(m.message_id);
    try {
      await api.delete(`/conversations/${active.conversation_id}/messages/${m.message_id}`);
      if (editingId === m.message_id) cancelEdit();
      await refreshAfterAction();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyMessageId(null);
    }
  };

  const onCreated = (convo) => {
    setConversations((cur) => {
      const exists = cur.some((c) => c.conversation_id === convo.conversation_id);
      return exists ? cur : [convo, ...cur];
    });
    setActiveId(convo.conversation_id);
    emitChange(MESSAGES_CHANGED);
  };

  const download = (att) => {
    downloadFile(att.download_path, att.filename).catch(() =>
      toast.error("Couldn't download that file")
    );
  };

  // Full-size photo viewer — closes if you switch conversations so it never
  // hangs onto a blob URL from a chat you've since left.
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (url, filename) => setLightbox({ url, filename });
  useEffect(() => {
    setLightbox(null);
    setReplyingTo(null);
  }, [activeId]);

  const subtitle = useMemo(() => {
    if (loading) return undefined;
    if (!conversations.length) return "Nothing yet";
    return unreadTotal ? `${unreadTotal} unread` : "You're all caught up";
  }, [loading, conversations.length, unreadTotal]);

  return (
    <Layout
      title="Messages"
      subtitle={subtitle}
      fullBleed
      action={
        <div className="flex items-center gap-2">
          {canViewAll && (
            <button
              onClick={() => setViewAll((v) => !v)}
              title="See every conversation in this business, not just your own"
              className={cn(
                "hidden sm:inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition-colors",
                viewAll
                  ? "border-brand bg-brand-light text-brand"
                  : "border-line text-muted2 hover:border-brand-ring hover:text-ink"
              )}
            >
              <Eye className="w-3.5 h-3.5" />
              Team inbox
            </button>
          )}
          <Button onClick={() => setCreating(true)} className="bg-brand hover:bg-brand-hover">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New message</span>
          </Button>
        </div>
      }
    >
      {loading ? (
        showLoading && <LoadingPanel rows={4} />
      ) : conversations.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={MessageSquare}
            title="No conversations yet"
            body="Message anyone on your team, one on one or in a group — text or files."
            action={
              <Button onClick={() => setCreating(true)} className="bg-brand hover:bg-brand-hover">
                <Plus className="w-4 h-4 mr-2" /> New message
              </Button>
            }
          />
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-0 lg:flex-1 lg:min-h-0 lg:bg-card lg:overflow-hidden">
          <div className="flex flex-col gap-1.5 lg:gap-0.5 lg:w-[240px] lg:shrink-0 lg:border-r lg:border-line lg:overflow-y-auto lg:p-2.5">
            {conversations.map((c) => {
              const isActive = c.conversation_id === activeId;
              const label = conversationLabel(c);
              const notMine = c.is_participant === false;
              return (
                <button
                  key={c.conversation_id}
                  onClick={() => setActiveId(c.conversation_id)}
                  className={cn(
                    "relative w-full text-left rounded-xl px-2.5 py-2.5 transition-colors flex items-center gap-2.5",
                    "border lg:border-0",
                    isActive
                      ? "border-brand bg-brand-light/60 lg:bg-brand-light"
                      : "border-line hover:border-brand-ring lg:hover:bg-surface"
                  )}
                >
                  {isActive && (
                    <span className="hidden lg:block absolute left-[-10px] top-2 bottom-2 w-[3px] rounded-full bg-brand" />
                  )}
                  <div className="relative shrink-0">
                    <div
                      className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center font-display font-semibold text-[12px]",
                        notMine ? "bg-surface text-muted2 border border-dashed border-line" : "bg-brand-light text-brand"
                      )}
                    >
                      {c.type === "group" ? <UsersIcon className="w-3.5 h-3.5" /> : conversationInitial(c)}
                    </div>
                    <OnlineDot show={otherParticipant(c)?.online} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <span
                        className={cn(
                          "text-[12.5px] truncate",
                          c.unread ? "font-semibold text-ink" : "font-medium text-ink"
                        )}
                      >
                        {label}
                      </span>
                      {notMine && <Eye className="w-3 h-3 text-muted2 shrink-0" title="You're not a participant" />}
                      {c.last_message_at && (
                        <span className="ml-auto text-[10px] text-muted2 shrink-0">
                          {ago(c.last_message_at)}
                        </span>
                      )}
                    </div>
                    <div className="text-[11.5px] text-muted2 mt-0.5 truncate">
                      {c.last_message_preview || "No messages yet"}
                    </div>
                  </div>
                  {c.unread && <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />}
                </button>
              );
            })}
          </div>

          {active && (
            <div className="panel lg:rounded-none lg:border-0 lg:shadow-none overflow-hidden flex flex-col min-w-0 h-[70vh] min-h-0 lg:h-auto lg:flex-1">
              <div className="px-5 py-3.5 border-b border-line lg:bg-card lg:shrink-0">
                <div className="flex items-center gap-2">
                  {active.type === "group" && <UsersIcon className="w-4 h-4 text-muted2" />}
                  {active.type !== "group" && (
                    <div className="relative shrink-0 w-7 h-7 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[11px]">
                      {conversationInitial(active)}
                      <OnlineDot show={otherParticipant(active)?.online} className="w-2 h-2" />
                    </div>
                  )}
                  <div>
                    <span className="text-[13.5px] font-medium text-ink block leading-tight">
                      {conversationLabel(active)}
                    </span>
                    {!isParticipant ? (
                      <span className="inline-flex items-center gap-1 text-[10.5px] text-muted2 leading-tight">
                        <Eye className="w-3 h-3" /> Viewing as admin — you're not in this conversation
                      </span>
                    ) : (
                      active.type !== "group" &&
                      otherParticipant(active)?.online && (
                        <span className="text-[10.5px] text-emerald-600 leading-tight">Online</span>
                      )
                    )}
                  </div>
                  {pinnedMessages.length > 0 && (
                    <Popover open={pinnedOpen} onOpenChange={setPinnedOpen}>
                      <PopoverTrigger asChild>
                        <button className="ml-auto inline-flex items-center gap-1 text-[11.5px] text-muted2 hover:text-brand transition-colors shrink-0">
                          <Pin className="w-3.5 h-3.5" />
                          {pinnedMessages.length} pinned
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0" align="end">
                        <div className="max-h-[320px] overflow-y-auto divide-y divide-line">
                          {pinnedMessages.map((m) => (
                            <button
                              key={m.message_id}
                              onClick={() => {
                                jumpToMessage(active.conversation_id, m.message_id);
                                setPinnedOpen(false);
                              }}
                              className="w-full text-left px-3 py-2.5 hover:bg-brand-light/40 transition-colors"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[12px] font-medium text-ink truncate">
                                  {m.sender_id === user?.user_id ? "You" : m.sender_name}
                                </span>
                                <span className="text-[10.5px] text-muted2 shrink-0">
                                  {when(m.created_at)}
                                </span>
                              </div>
                              <div className="text-[12px] text-muted2 truncate mt-0.5">
                                {m.deleted
                                  ? "Message deleted"
                                  : m.body ||
                                    (m.attachments?.[0]?.filename
                                      ? `📎 ${m.attachments[0].filename}`
                                      : "")}
                              </div>
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                {active.type === "group" && (
                  <div className="text-[11.5px] text-muted2 mt-0.5 truncate flex flex-wrap items-center gap-x-1.5">
                    {active.participants.map((p) => (
                      <span key={p.user_id} className="inline-flex items-center gap-1">
                        {p.online && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                        {p.you ? "You" : p.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative flex-1 min-h-0 flex flex-col">
              <div
                ref={threadRef}
                onScroll={onThreadScroll}
                className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4 bg-surface/40 [overflow-anchor:none]"
              >
                {messages.length > 0 &&
                  (loadingOlder ? (
                    <div className="flex items-center justify-center gap-2 py-1 text-[12px] text-muted2" role="status">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Loading earlier messages…
                    </div>
                  ) : hasOlder ? (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={loadOlder}
                        className="text-[12px] font-medium text-muted2 hover:text-brand border border-line hover:border-brand-ring bg-card rounded-full px-3 py-1 transition-colors"
                      >
                        Load earlier messages
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center text-center gap-1 pt-2 pb-1">
                      <span className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[15px]">
                        {conversationInitial(active)}
                      </span>
                      <span className="text-[13px] font-semibold text-ink">
                        Start of your conversation{active.type === "group" ? " in " : " with "}
                        {conversationLabel(active)}
                      </span>
                      <span className="text-[11.5px] text-muted2">
                        Began {new Date(messages[0].created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                      </span>
                    </div>
                  ))}
                {threadLoading && messages.length === 0 ? (
                  <div className="flex justify-center py-10">
                    <div className="w-5 h-5 rounded-full border-2 border-brand/30 border-t-brand animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-[12.5px] text-muted2 py-10">
                    Say hello — nothing here yet.
                  </div>
                ) : (
                  messages.map((m, i) => {
                    const mine = m.sender_id === user?.user_id;
                    const day = dayKey(m.created_at);
                    const newDay = i === 0 || day !== dayKey(messages[i - 1].created_at);
                    const isEditing = editingId === m.message_id;
                    const reactionEntries = Object.entries(m.reactions || {}).filter(
                      ([, ids]) => ids && ids.length > 0
                    );
                    return (
                      <Fragment key={m.message_id}>
                      {newDay && (
                        <div className="flex justify-center" role="separator" aria-label={dayLabel(m.created_at)}>
                          <span className="text-[11px] font-medium text-muted2 bg-card border border-line rounded-full px-2.5 py-0.5">
                            {dayLabel(m.created_at)}
                          </span>
                        </div>
                      )}
                      <div
                        id={`msg-${m.message_id}`}
                        className={cn(
                          "flex flex-col max-w-[75%] group/msg",
                          mine ? "ml-auto items-end" : "mr-auto items-start"
                        )}
                      >
                        <div className={cn("flex items-baseline gap-2", mine && "justify-end")}>
                          <span
                            className={cn(
                              "text-[12.5px] font-medium",
                              mine ? "text-brand" : "text-ink"
                            )}
                          >
                            {mine ? "You" : m.sender_name}
                          </span>
                          <span className="text-[11px] text-muted2">{when(m.created_at)}</span>
                          {m.edited_at && !m.deleted && (
                            <span className="text-[10.5px] text-muted2 italic">(edited)</span>
                          )}
                          {m.pinned && !m.deleted && (
                            <span title="Pinned" aria-label="Pinned" className="inline-flex">
                              <Pin className="w-3 h-3 text-brand" />
                            </span>
                          )}
                          {!m.deleted && isParticipant && (
                            <div className={cn("flex items-center gap-0.5", mine && "order-first")}>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    className="p-1 rounded text-muted2 hover:text-brand hover:bg-brand-light/50 transition-colors"
                                    title="React"
                                    aria-label="React to this message"
                                  >
                                    <Smile className="w-3.5 h-3.5" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-1.5 flex gap-1" align={mine ? "end" : "start"}>
                                  {QUICK_REACTIONS.map((emoji) => (
                                    <button
                                      key={emoji}
                                      onClick={() => toggleReaction(m.message_id, emoji)}
                                      className="text-[16px] leading-none p-1 rounded hover:bg-brand-light/60 transition-colors"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </PopoverContent>
                              </Popover>
                              <button
                                onClick={() => startReply(m)}
                                className="p-1 rounded text-muted2 hover:text-brand hover:bg-brand-light/50 transition-colors"
                                title="Reply"
                                aria-label="Reply to this message"
                              >
                                <Reply className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => togglePin(m.message_id)}
                                className={cn(
                                  "p-1 rounded transition-colors",
                                  m.pinned
                                    ? "text-brand hover:bg-brand-light/50"
                                    : "text-muted2 hover:text-brand hover:bg-brand-light/50"
                                )}
                                title={m.pinned ? "Unpin" : "Pin"}
                                aria-label={m.pinned ? "Unpin message" : "Pin message"}
                              >
                                <Pin className="w-3.5 h-3.5" />
                              </button>
                              {mine && (
                                <>
                                  <button
                                    onClick={() => (isEditing ? cancelEdit() : startEdit(m))}
                                    className="p-1 rounded text-muted2 hover:text-brand hover:bg-brand-light/50 transition-colors"
                                    title="Edit"
                                    aria-label="Edit message"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => deleteMessage(m)}
                                    disabled={busyMessageId === m.message_id}
                                    className="p-1 rounded text-muted2 hover:text-stock-out hover:bg-rose-50 transition-colors"
                                    title="Delete"
                                    aria-label="Delete message"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        {m.deleted ? (
                          <div className="text-[13px] text-muted2 italic mt-0.5">Message deleted</div>
                        ) : isEditing ? (
                          <div className="mt-0.5 space-y-1.5 w-full max-w-[360px]">
                            <Textarea
                              autoFocus
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  saveEdit(m.message_id);
                                } else if (e.key === "Escape") {
                                  cancelEdit();
                                }
                              }}
                              className="text-[13.5px] min-h-[60px] w-full bg-card"
                            />
                            <div className={cn("flex gap-1.5", mine && "justify-end")}>
                              <Button
                                size="sm"
                                className="h-7 px-2.5 bg-brand hover:bg-brand-hover"
                                disabled={!editText.trim() || busyMessageId === m.message_id}
                                onClick={() => saveEdit(m.message_id)}
                              >
                                <Check className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2.5" onClick={cancelEdit}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {(m.body || m.reply_to) && (
                              <div
                                className={cn(
                                  "mt-0.5 rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed transition-shadow duration-700",
                                  mine
                                    ? "bg-brand text-white rounded-tr-sm"
                                    : "bg-card border border-line text-ink shadow-card rounded-tl-sm",
                                  highlightedId === m.message_id &&
                                    "ring-2 ring-brand ring-offset-2 ring-offset-surface"
                                )}
                              >
                                {m.reply_to && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      active && jumpToMessage(active.conversation_id, m.reply_to.message_id)
                                    }
                                    className={cn(
                                      "block w-full text-left rounded-lg px-2 py-1.5 mb-1.5 border-l-[3px] text-[12px] transition-colors",
                                      mine
                                        ? "bg-white/15 border-white/70 hover:bg-white/20"
                                        : "bg-brand-light/60 border-brand hover:bg-brand-light"
                                    )}
                                  >
                                    <div className={cn("font-medium", mine ? "text-white" : "text-brand")}>
                                      {m.reply_to.sender_id === user?.user_id ? "You" : m.reply_to.sender_name}
                                    </div>
                                    <div
                                      className={cn(
                                        "whitespace-pre-wrap break-words",
                                        mine ? "text-white/80" : "text-muted2"
                                      )}
                                    >
                                      {m.reply_to.body ||
                                        (m.reply_to.attachment_filename
                                          ? `📎 ${m.reply_to.attachment_filename}`
                                          : "Message")}
                                    </div>
                                  </button>
                                )}
                                {m.body && (
                                  <div className="whitespace-pre-wrap">
                                    {renderMessageBody(m.body, m.mentions, active?.participants)}
                                  </div>
                                )}
                              </div>
                            )}
                            {m.attachments?.length > 0 && (
                              <div className={cn("mt-1.5 space-y-1.5", mine ? "flex flex-col items-end" : "flex flex-col items-start")}>
                                {m.attachments.map((att) =>
                                  att.content_type?.startsWith("image/") ? (
                                    <AttachmentImage key={att.attachment_id} att={att} onOpen={openLightbox} />
                                  ) : att.content_type?.startsWith("audio/") ? (
                                    <AttachmentAudio key={att.attachment_id} att={att} mine={mine} />
                                  ) : (
                                    <button
                                      key={att.attachment_id}
                                      onClick={() => download(att)}
                                      className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[12px] text-ink hover:border-brand-ring transition-colors max-w-full shadow-card"
                                      title={`Download ${att.filename}`}
                                    >
                                      <FileIcon className="w-3.5 h-3.5 text-muted2 shrink-0" />
                                      <span className="truncate max-w-[180px]">{att.filename}</span>
                                      <span className="text-muted2 shrink-0">{bytesToSize(att.size)}</span>
                                      <Download className="w-3 h-3 text-muted2 shrink-0" />
                                    </button>
                                  )
                                )}
                              </div>
                            )}
                            {reactionEntries.length > 0 && (
                              <div className={cn("mt-1.5 flex flex-wrap gap-1", mine && "justify-end")}>
                                {reactionEntries.map(([emoji, ids]) => {
                                  const mineReacted = ids.includes(user?.user_id);
                                  return (
                                    <button
                                      key={emoji}
                                      onClick={() => isParticipant && toggleReaction(m.message_id, emoji)}
                                      disabled={!isParticipant}
                                      className={cn(
                                        "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11.5px] transition-colors",
                                        mineReacted
                                          ? "border-brand bg-brand-light text-brand"
                                          : "border-line bg-card text-muted2 hover:border-brand-ring",
                                        !isParticipant && "cursor-default hover:border-line"
                                      )}
                                    >
                                      <span>{emoji}</span>
                                      <span className="font-mono">{ids.length}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      </Fragment>
                    );
                  })
                )}
              </div>
              {showJump && messages.length > 0 && (
                <button
                  type="button"
                  onClick={jumpToLatest}
                  className="absolute right-4 bottom-3 flex items-center gap-1.5 rounded-full border border-line bg-card text-ink text-[12px] font-medium pl-2.5 pr-3 py-1.5 shadow-lift hover:border-brand-ring transition-colors"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                  Jump to latest
                </button>
              )}
              </div>

              {!isParticipant ? (
                <div className="border-t border-line px-5 py-3.5 shrink-0 bg-card text-[12.5px] text-muted2 flex items-center gap-2">
                  <Eye className="w-4 h-4 shrink-0" />
                  You can read this conversation because you're an admin, but you're not a participant —
                  reacting, pinning and replying are turned off here.
                </div>
              ) : (
              <div className="border-t border-line px-5 py-3.5 space-y-2 shrink-0 bg-card">
                {replyingTo && (
                  <div className="flex items-center gap-2 rounded-lg bg-brand-light/50 border-l-[3px] border-brand pl-2.5 pr-1.5 py-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11.5px] font-medium text-brand">
                        Replying to {replyingTo.sender_id === user?.user_id ? "yourself" : replyingTo.sender_name}
                      </div>
                      <div className="text-[12px] text-muted2 truncate">
                        {replyingTo.body ||
                          (replyingTo.attachments?.[0]?.filename
                            ? `📎 ${replyingTo.attachments[0].filename}`
                            : "Message")}
                      </div>
                    </div>
                    <button
                      onClick={cancelReply}
                      className="p-1 rounded-lg text-muted2 hover:text-ink hover:bg-brand-light shrink-0"
                      aria-label="Cancel reply"
                      title="Cancel reply"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {typingUsers.length > 0 && (
                  <div className="text-[11.5px] text-muted2 italic">
                    {typingUsers.length === 1
                      ? `${typingUsers[0].name} is typing…`
                      : typingUsers.length === 2
                      ? `${typingUsers[0].name} and ${typingUsers[1].name} are typing…`
                      : `${typingUsers.length} people are typing…`}
                  </div>
                )}
                {pendingFiles.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {pendingFiles.map((f, i) =>
                      pendingAudioUrls[i] ? (
                        <div key={`${f.name}-${i}`} className="flex items-center gap-1">
                          <AudioPlayer src={pendingAudioUrls[i]} />
                          <button
                            onClick={() => removeFile(i)}
                            className="p-1 rounded text-muted2 hover:text-stock-out hover:bg-rose-50 transition-colors shrink-0"
                            aria-label={`Remove ${f.name}`}
                            title="Remove"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <span
                          key={`${f.name}-${i}`}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-light text-brand px-2 py-1 text-[11.5px]"
                        >
                          <FileIcon className="w-3 h-3" />
                          <span className="truncate max-w-[140px]">{f.name}</span>
                          <button
                            onClick={() => removeFile(i)}
                            className="hover:text-stock-out"
                            aria-label={`Remove ${f.name}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )
                    )}
                  </div>
                )}
                <div className="flex gap-2 items-end">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  {recording ? (
                    <div
                      className={cn(
                        "flex-1 min-w-0 flex items-center gap-2.5 border rounded-md pl-2 pr-1 h-10 transition-colors",
                        recordPaused
                          ? "bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800"
                          : "bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:border-rose-800"
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => stopRecording({ discard: true })}
                        className={cn(
                          "p-1 rounded shrink-0 transition-colors",
                          recordPaused ? "text-amber-700 hover:bg-amber-100" : "text-rose-600 hover:bg-rose-100"
                        )}
                        aria-label="Discard recording"
                        title="Discard recording"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {recordPaused ? (
                        <span className="h-2 w-2 rounded-[2px] bg-amber-600 shrink-0" aria-hidden="true" />
                      ) : (
                        <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
                        </span>
                      )}
                      <span
                        className={cn(
                          "text-[12.5px] font-mono tabular-nums shrink-0",
                          recordPaused ? "text-amber-800 dark:text-amber-300" : "text-rose-700 dark:text-rose-300"
                        )}
                        aria-live="polite"
                      >
                        <span className="sr-only">{recordPaused ? "Paused at " : "Recording, "}</span>
                        {formatDuration(recordSeconds)}
                      </span>
                      <div className="flex-1 min-w-0 flex items-center justify-end gap-[2px] h-6 overflow-hidden" aria-hidden="true">
                        {liveLevels.map((lv, i) => (
                          <span
                            key={i}
                            className={cn(
                              "w-[3px] shrink-0 rounded-full",
                              recordPaused ? "bg-amber-500/70" : "bg-rose-500"
                            )}
                            style={{ height: `${Math.max(12, Math.round(lv * 100))}%` }}
                          />
                        ))}
                      </div>
                      {canPauseRecording &&
                        (recordPaused ? (
                          <button
                            type="button"
                            onClick={resumeRecording}
                            className="h-8 pl-2 pr-2.5 rounded-full border border-amber-300 bg-card text-amber-700 hover:bg-amber-50 flex items-center gap-1 text-[12px] font-semibold shrink-0 transition-colors"
                            aria-label="Resume recording"
                            title="Resume recording"
                          >
                            <Mic className="w-3.5 h-3.5" />
                            Resume
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={pauseRecording}
                            className="w-8 h-8 rounded-full border border-rose-300 bg-card text-rose-600 hover:bg-rose-50 flex items-center justify-center shrink-0 transition-colors"
                            aria-label="Pause recording"
                            title="Pause recording"
                          >
                            <Pause className="w-3.5 h-3.5" fill="currentColor" />
                          </button>
                        ))}
                      <button
                        type="button"
                        onClick={() => stopRecording()}
                        className="w-8 h-8 rounded-full border border-rose-300 bg-card text-rose-600 hover:bg-rose-50 flex items-center justify-center shrink-0 transition-colors"
                        aria-label="Stop and review"
                        title="Stop and review before sending"
                      >
                        <Square className="w-3 h-3" fill="currentColor" />
                      </button>
                      <button
                        type="button"
                        onClick={() => stopRecording({ sendNow: true })}
                        disabled={sending}
                        className="w-9 h-9 rounded-full bg-brand hover:bg-brand-hover text-white flex items-center justify-center shrink-0 transition-colors shadow-card disabled:opacity-60"
                        aria-label="Send voice note now"
                        title={
                          text.trim() || pendingFiles.length
                            ? "Send now, with your message and attachments"
                            : "Send now"
                        }
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-9 px-2.5 text-muted2 shrink-0"
                    aria-label="Attach a file"
                    title="Attach a file"
                  >
                    <Paperclip className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={startRecording}
                    disabled={!micSupported || sending}
                    className="h-9 px-2.5 text-muted2 shrink-0"
                    aria-label="Record a voice note"
                    title={micSupported ? "Record a voice note" : "Voice notes aren't supported in this browser"}
                  >
                    <Mic className="w-4 h-4" />
                  </Button>
                  <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-9 px-2.5 text-muted2 shrink-0"
                        aria-label="Add an emoji or sticker"
                        title="Add an emoji or sticker"
                      >
                        <Smile className="w-4 h-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-1.5" align="start">
                      <div className="flex gap-1 mb-1.5 px-0.5">
                        <button
                          type="button"
                          onClick={() => setPickerMode("emoji")}
                          className={`flex-1 text-xs font-medium rounded-md px-2 py-1 transition-colors ${
                            pickerMode === "emoji"
                              ? "bg-brand-light text-brand"
                              : "text-muted2 hover:bg-muted/60"
                          }`}
                        >
                          Emoji
                        </button>
                        <button
                          type="button"
                          onClick={() => setPickerMode("sticker")}
                          className={`flex-1 text-xs font-medium rounded-md px-2 py-1 transition-colors ${
                            pickerMode === "sticker"
                              ? "bg-brand-light text-brand"
                              : "text-muted2 hover:bg-muted/60"
                          }`}
                        >
                          Stickers
                        </button>
                      </div>
                      {pickerMode === "emoji" ? (
                        <EmojiPicker onPick={insertEmoji} />
                      ) : (
                        <StickerPicker onPick={sendSticker} busy={sending} />
                      )}
                    </PopoverContent>
                  </Popover>
                  <Popover open={mentionQuery !== null && mentionCandidates.length > 0}>
                    <PopoverAnchor asChild>
                      <Textarea
                        ref={textInputRef}
                        rows={1}
                        value={text}
                        onChange={handleTextChange}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                          } else if (e.key === "Escape" && mentionQuery !== null) {
                            setMentionQuery(null);
                          }
                        }}
                        placeholder="Write a message… (@ to mention someone)"
                        aria-label="Message"
                        className="flex-1 min-w-0 min-h-9 h-9 py-[7px] leading-5 resize-none overflow-y-hidden"
                      />
                    </PopoverAnchor>
                    <PopoverContent
                      className="w-64 p-1"
                      align="start"
                      onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                      {mentionCandidates.map((p) => (
                        <button
                          key={p.user_id}
                          type="button"
                          onClick={() => insertMention(p)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-[12.5px] hover:bg-brand-light/50 transition-colors"
                        >
                          <span className="w-6 h-6 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[10.5px] shrink-0">
                            {(p.name?.[0] || "?").toUpperCase()}
                          </span>
                          <span className="truncate">{p.name}</span>
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                  <Button
                    onClick={() => send()}
                    disabled={sending || (!text.trim() && pendingFiles.length === 0)}
                    className="bg-brand hover:bg-brand-hover shrink-0"
                    aria-label="Send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                    </>
                  )}
                </div>
                {!recording && text.length > 0 && (
                  <p className="hidden sm:block text-[11px] text-muted2 pl-[132px] -mt-0.5">
                    <kbd className="font-mono text-[10px] bg-surface border border-line rounded px-1">Enter</kbd> to send ·{" "}
                    <kbd className="font-mono text-[10px] bg-surface border border-line rounded px-1">Shift</kbd> +{" "}
                    <kbd className="font-mono text-[10px] bg-surface border border-line rounded px-1">Enter</kbd> for a new line
                  </p>
                )}
              </div>
              )}
            </div>
          )}
        </div>
      )}

      <NewConversationDialog open={creating} onOpenChange={setCreating} onCreated={onCreated} />

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 sm:p-8"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            title="Close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <a
            href={lightbox.url}
            download={lightbox.filename}
            onClick={(e) => e.stopPropagation()}
            title={`Download ${lightbox.filename}`}
            className="absolute top-4 left-4 sm:top-6 sm:left-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <Download className="w-5 h-5" />
          </a>
          <img
            src={lightbox.url}
            alt={lightbox.filename}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full rounded-lg shadow-2xl object-contain"
          />
        </div>
      )}
    </Layout>
  );
}
